import React, { useState, useEffect, useRef, useCallback } from 'react';
import AppLayout from '../../components/AppLayout';
import api from '../../api/axiosInstance';
import './PreOpenMarket.css';

/* ─────────────────────────────────────────────
   Constants
───────────────────────────────────────────── */
const CATEGORIES = [
  { key: 'NIFTY50', label: 'NIFTY 50'             },
  { key: 'FO',      label: 'Securities in F&O'    },
  { key: 'CASH',    label: 'CASH (All − F&O)'     },
  { key: 'CAS',     label: 'CAS (Closing Auction)'},
];

const COLS = [
  { key: 'symbol',            label: 'Symbol'        },
  { key: 'previousClose',     label: 'Prev Close'    },
  { key: 'iep',               label: 'IEP'           },
  { key: 'finalPrice',        label: 'Final Price'   },
  { key: 'change',            label: 'Change'        },
  { key: 'pChange',           label: '% Change'      },
  { key: 'finalQuantity',     label: 'Final Qty'     },
  { key: 'finalValue',        label: 'Turnover (₹L)' },
  { key: 'ffmMarketCap',      label: 'FFM Cap (Cr)'  },
  { key: 'totalBuyQuantity',  label: 'Buy Qty'       },
  { key: 'totalSellQuantity', label: 'Sell Qty'      },
  { key: 'yearHigh',          label: '52W High'      },
  { key: 'yearLow',           label: '52W Low'       },
];

// CAS-only column: NSE's pre-CAS regular-session close (basis for change/%change).
const CAS_REF_COL = { key: 'referencePrice', label: 'Ref Price' };

function getColsForCategory(cat) {
  if (cat !== 'CAS') return COLS;
  // Insert Ref Price right after Prev Close.
  const idx = COLS.findIndex(c => c.key === 'previousClose');
  return [...COLS.slice(0, idx + 1), CAS_REF_COL, ...COLS.slice(idx + 1)];
}

/* ─────────────────────────────────────────────
   Formatters
───────────────────────────────────────────── */
function fmtPrice(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtNum(v) {
  if (v == null || v === 0) return '—';
  return Number(v).toLocaleString('en-IN');
}

function fmtTurnover(v) {
  if (v == null || v === 0) return '—';
  const lakh = v / 1e5;
  return `₹${lakh.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} L`;
}

function fmtMarketCap(v) {
  if (v == null || v === 0) return '—';
  const cr = v / 1e7;
  if (cr >= 1e5) return `${(cr / 1e5).toFixed(2)} L Cr`;
  return `${cr.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Cr`;
}

/* ─────────────────────────────────────────────
   Cell renderers
───────────────────────────────────────────── */
function SymbolCell({ stock }) {
  return (
    <span className="pom-sym-cell">
      {stock.symbol || '—'}
      {stock.series && <span className="pom-near" title="Series">{stock.series}</span>}
    </span>
  );
}

function ChangeCell({ value, isPct }) {
  if (value == null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const cls = value > 0 ? 'pos' : value < 0 ? 'neg' : 'zero';
  const prefix = value > 0 ? '+' : '';
  return (
    <span className={`pom-chg ${cls}`}>
      {prefix}{value.toFixed(2)}{isPct ? '%' : ''}
    </span>
  );
}

function renderCell(col, stock) {
  const v = stock[col.key];
  switch (col.key) {
    case 'symbol':            return <SymbolCell stock={stock} />;
    case 'change':            return <ChangeCell value={v} />;
    case 'pChange':           return <ChangeCell value={v} isPct />;
    case 'previousClose':
    case 'referencePrice':
    case 'iep':
    case 'finalPrice':
    case 'yearHigh':
    case 'yearLow':           return <span className="pom-price-cell">{fmtPrice(v)}</span>;
    case 'finalQuantity':
    case 'totalBuyQuantity':
    case 'totalSellQuantity': return <span className="pom-num-cell">{fmtNum(v)}</span>;
    case 'finalValue':        return <span className="pom-num-cell">{fmtTurnover(v)}</span>;
    case 'ffmMarketCap':      return <span className="pom-num-cell">{fmtMarketCap(v)}</span>;
    default:                  return <span style={{ color: 'var(--text-muted)' }}>{v ?? '—'}</span>;
  }
}

/* ─────────────────────────────────────────────
   Sync Progress Component
───────────────────────────────────────────── */
function SyncBar({ state }) {
  // state: null | { msg, pct, indeterminate, detail, done, isError }
  if (!state) return null;
  return (
    <div className={`pom-sync-bar visible`}>
      <div className="pom-sync-label">
        <span>{state.msg}</span>
        <span>{state.indeterminate ? '' : `${state.pct}%`}</span>
      </div>
      <div className="pom-sync-track">
        <div
          className={`pom-sync-fill${state.indeterminate ? ' indeterminate' : ''}`}
          style={state.indeterminate ? {} : { width: `${state.pct}%`, background: state.isError ? 'linear-gradient(90deg,#ff6b6b,#ee5a24)' : undefined }}
        />
      </div>
      {state.detail && (
        <div className="pom-sync-detail" style={{ display: 'block' }}>{state.detail}</div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Toast hook
───────────────────────────────────────────── */
function useToast() {
  const [toast, setToast] = useState(null); // { type, msg }
  const timerRef = useRef(null);

  const show = useCallback((type, msg) => {
    setToast({ type, msg, show: true });
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  return { toast, show };
}

/* ─────────────────────────────────────────────
   Main Page
───────────────────────────────────────────── */
export default function PreOpenMarket() {
  const [category, setCategory] = useState('NIFTY50');
  const [allStocks, setAllStocks]       = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [summaryData, setSummaryData]   = useState(null); // eslint-disable-line no-unused-vars
  const [metaData, setMetaData]         = useState(null);   // { tradeDate, nseTimestamp, fetchedAt, totalStocks }
  const [searchQ, setSearchQ]           = useState('');
  const [sortKey, setSortKey]           = useState(null);
  const [sortDir, setSortDir]           = useState(1);
  const [pageStatus, setPageStatus]     = useState('idle'); // idle | loading | error
  const [errorMsg, setErrorMsg]         = useState('');
  const [fetchLoading, setFetchLoading] = useState(false);
  const [casFetchLoading, setCasFetchLoading] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [syncState, setSyncState]       = useState(null);
  const [selectedDate, setSelectedDate] = useState('');   // '' = latest
  const [availableDates, setAvailableDates] = useState([]);

  const { toast, show: showToast } = useToast();

  /* ── apply filter + sort ── */
  useEffect(() => {
    let list = searchQ.trim()
      ? allStocks.filter(s => (s.symbol || '').toLowerCase().includes(searchQ.toLowerCase()))
      : [...allStocks];

    if (sortKey) {
      list.sort((a, b) => {
        const av = a[sortKey], bv = b[sortKey];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'string') return sortDir * av.localeCompare(bv);
        return sortDir * (av - bv);
      });
    }

    setFilteredStocks(list);
  }, [allStocks, searchQ, sortKey, sortDir]);

  /* ── load saved data (latest, or a specific tradeDate for backtesting) ── */
  const loadData = useCallback(async (cat, date) => {
    const activeCat = cat || category;
    setPageStatus('loading');
    setMetaData(null);
    setSummaryData(null);
    setAllStocks([]);
    setSortKey(null);
    setSortDir(1);
    try {
      const url = date
        ? `/pre-open/by-date?category=${activeCat}&date=${date}`
        : `/pre-open/latest?category=${activeCat}`;
      const res = await api.get(url);
      const doc = res.data;
      if (!doc.success) throw new Error(doc.message || 'No data');
      setMetaData({
        tradeDate:    doc.tradeDate,
        nseTimestamp: doc.nseTimestamp,
        fetchedAt:    doc.fetchedAt,
        totalStocks:  doc.totalStocks,
      });
      setSummaryData(doc.indexSummary || {});
      setAllStocks(doc.stocks || []);
      setPageStatus('idle');
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setPageStatus('empty');
      } else {
        setErrorMsg(err.response?.data?.message || err.message || 'Failed to load data');
        setPageStatus('error');
        showToast('error', `❌ ${err.response?.data?.message || err.message}`);
      }
    }
  }, [category, showToast]);

  const loadLatest = useCallback((cat) => loadData(cat, ''), [loadData]);

  /* ── load list of available snapshot dates for the current category ── */
  const loadAvailableDates = useCallback(async (cat) => {
    const activeCat = cat || category;
    try {
      const res = await api.get(`/pre-open/available-dates?category=${activeCat}`);
      if (res.data?.success) setAvailableDates(res.data.dates || []);
      else setAvailableDates([]);
    } catch {
      setAvailableDates([]);
    }
  }, [category]);

  /* ── initial load ── */
  useEffect(() => {
    loadData(category, selectedDate);
    loadAvailableDates(category);
  }, [category]); // eslint-disable-line

  /* ── switch category ── */
  const handleCatChange = (key) => {
    setCategory(key);
    setSearchQ('');
    setSelectedDate('');
  };

  /* ── apply/clear date filter ── */
  const handleApplyDate = () => {
    loadData(category, selectedDate);
  };
  const handleClearDate = () => {
    setSelectedDate('');
    loadData(category, '');
  };

  /* ── fetch from NSE ── */
  const handleFetchFromNSE = async () => {
    setFetchLoading(true);
    showToast('info', '⬇ Fetching live data from NSE…');

    // Step 1 – fetch
    setSyncState({ msg: 'Step 1 of 2 — Fetching from NSE…', pct: 0, indeterminate: true });
    try {
      const res = await api.get('/pre-open/fetch');
      if (!res.data.success) throw new Error(res.data.message || 'Fetch failed');
      const count = res.data.data?.length ?? 0;
      showToast('success', `✅ Received ${count} categories from NSE`);

      // Step 2 – sync
      setSyncState({ msg: 'Step 2 of 2 — Syncing to database collections…', pct: 50, indeterminate: false });
      try {
        const syncRes = await api.get('/pre-open/sync-csv');
        if (syncRes.data.success) {
          const total   = syncRes.data.summary?.reduce((acc, s) => acc + (s.upserted || 0), 0) ?? 0;
          const details = syncRes.data.summary?.map(s => `${s.category}: ${s.upserted} records → ${s.collection}`).join(' | ') || '';
          setSyncState({ msg: `✅ Sync complete — ${total} records saved`, pct: 100, indeterminate: false, detail: details });
          showToast('success', `💾 Synced ${total} records to DB`);
        } else {
          setSyncState({ msg: '⚠ Sync completed with warnings', pct: 100, indeterminate: false, isError: true, detail: syncRes.data.message || '' });
          showToast('error', `⚠ Sync: ${syncRes.data.message || 'unknown error'}`);
        }
      } catch (syncErr) {
        setSyncState({ msg: '❌ CSV sync failed — NSE data was saved', pct: 100, indeterminate: false, isError: true, detail: syncErr.message });
        showToast('error', `⚠ Sync failed: ${syncErr.message}`);
      }

      // Refresh table
      await loadLatest(category);
      loadAvailableDates(category);
    } catch (err) {
      setSyncState(null);
      showToast('error', `❌ ${err.response?.data?.message || err.message}`);
    } finally {
      setFetchLoading(false);
      // Auto-hide sync bar
      setTimeout(() => setSyncState(null), 4000);
    }
  };

  /* ── fetch CAS (Closing Auction Session) from NSE ── */
  const handleFetchCAS = async () => {
    setCasFetchLoading(true);
    showToast('info', '⬇ Fetching CAS data from NSE…');
    setSyncState({ msg: 'Fetching CAS from NSE…', pct: 0, indeterminate: true });
    try {
      const res = await api.get('/pre-open/cas/fetch');
      if (!res.data.success) throw new Error(res.data.message || 'CAS fetch failed');
      const total = res.data.data?.totalStocks ?? 0;
      const upserted = res.data.data?.upserted ?? 0;
      setSyncState({
        msg: `✅ CAS complete — ${total} stocks fetched, ${upserted} saved`,
        pct: 100,
        indeterminate: false,
      });
      showToast('success', `✅ CAS: ${total} stocks (${upserted} saved to DB)`);
      // Auto-switch to CAS tab & refresh
      setCategory('CAS');
      setSelectedDate('');
      await loadLatest('CAS');
      loadAvailableDates('CAS');
    } catch (err) {
      setSyncState({
        msg: '❌ CAS fetch failed',
        pct: 100,
        indeterminate: false,
        isError: true,
        detail: err.response?.data?.message || err.message,
      });
      showToast('error', `❌ ${err.response?.data?.message || err.message}`);
    } finally {
      setCasFetchLoading(false);
      setTimeout(() => setSyncState(null), 4000);
    }
  };

  /* ── sort ── */
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d * -1);
    } else {
      setSortKey(key);
      setSortDir(1);
    }
  };

  const displayCols = getColsForCategory(category);

  const sortLabel = sortKey
    ? `Sort: ${displayCols.find(c => c.key === sortKey)?.label || sortKey} ${sortDir === 1 ? '↑' : '↓'}`
    : 'Sort: Default';

  /* ─── render ─── */
  return (
    <AppLayout pageBadge="📊 Fetch Market Data">
      <div className="pom-root">

        {/* ── Controls ── */}
        <div className="pom-controls-row">
          <div className="pom-controls-left">
            <div className="pom-tab-bar">
              {CATEGORIES.map(c => (
                <button
                  key={c.key}
                  className={`pom-tab-btn${category === c.key ? ' active' : ''}`}
                  onClick={() => handleCatChange(c.key)}
                  disabled={fetchLoading}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="pom-date-filter" title="Backtesting: view a historical snapshot">
              <label className="pom-date-label">📅 Trade Date</label>
              <input
                type="date"
                className="pom-date-input"
                list="pom-available-dates"
                value={selectedDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={e => setSelectedDate(e.target.value)}
                disabled={fetchLoading || casFetchLoading || pageStatus === 'loading'}
              />
              <datalist id="pom-available-dates">
                {availableDates.map(d => <option key={d} value={d} />)}
              </datalist>
              <button
                className="pom-btn pom-btn-ghost pom-btn-sm"
                onClick={handleApplyDate}
                disabled={!selectedDate || fetchLoading || casFetchLoading || pageStatus === 'loading'}
              >
                Apply
              </button>
              <button
                className="pom-btn pom-btn-ghost pom-btn-sm"
                onClick={handleClearDate}
                disabled={!selectedDate || fetchLoading || casFetchLoading || pageStatus === 'loading'}
                title="Clear date and load latest"
              >
                Latest
              </button>
              {availableDates.length > 0 && (
                <span className="pom-date-hint">{availableDates.length} snapshot{availableDates.length === 1 ? '' : 's'} available</span>
              )}
            </div>
          </div>

          <div className="pom-controls-right">
            <input
              type="text"
              className="pom-search"
              placeholder="🔍  Search symbol…"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
            />
            <button
              className="pom-btn pom-btn-ghost"
              onClick={() => loadData(category, selectedDate)}
              disabled={fetchLoading || casFetchLoading || pageStatus === 'loading'}
            >
              ↻ Refresh
            </button>
            <button
              className="pom-btn pom-btn-primary"
              onClick={handleFetchFromNSE}
              disabled={fetchLoading}
            >
              {fetchLoading
                ? <><span className="pom-btn-spinner" /> Fetching…</>
                : '⬇ Fetch from NSE'}
            </button>
            <button
              className="pom-btn pom-btn-primary"
              onClick={handleFetchCAS}
              disabled={casFetchLoading}
              title="Fetch Closing Auction Session data from NSE"
            >
              {casFetchLoading
                ? <><span className="pom-btn-spinner" /> Fetching CAS…</>
                : '⬇ Fetch CAS from NSE'}
            </button>
            <button
              className="pom-btn pom-btn-danger"
              onClick={() => setPurgeOpen(true)}
              disabled={fetchLoading || casFetchLoading}
              title="Delete old snapshots from pre_open_data (CsvData collections are not touched)"
            >
              🗑 Purge Old Data
            </button>
          </div>
        </div>

        {/* ── Sync progress ── */}
        <SyncBar state={syncState} />

        {/* ── Info bar ── */}
        {metaData && (
          <div className="pom-info-bar visible">
            <span>📅 <strong>{metaData.tradeDate || '—'}</strong></span>
            <span>🕐 <strong>{metaData.nseTimestamp || '—'}</strong></span>
            <span>💾 <strong>
              {metaData.fetchedAt ? new Date(metaData.fetchedAt).toLocaleTimeString('en-IN') : '—'}
            </strong></span>
            <span>📦 <strong>{metaData.totalStocks ?? '—'}</strong> stocks</span>
          </div>
        )}

        {/* ── Table ── */}
        <div className="pom-table-wrap">
          <div className="pom-table-head-bar">
            <div className="pom-table-title">
              Stocks
              <span className="pom-stock-count">{filteredStocks.length}</span>
            </div>
            <div className="pom-sort-label">{sortLabel}</div>
          </div>

          <div className="pom-table-scroll">
            {/* Loading */}
            {pageStatus === 'loading' && (
              <div className="pom-state-box">
                <span className="pom-spinner" />
                <div className="pom-state-title">Loading…</div>
              </div>
            )}

            {/* Empty */}
            {pageStatus === 'empty' && (
              <div className="pom-state-box">
                <div className="pom-state-icon">📭</div>
                <div className="pom-state-title">No saved data</div>
                <div className="pom-state-msg">
                  No saved pre-open data for <strong>{category}</strong>.
                  Click <strong>Fetch from NSE</strong> to download it.
                </div>
              </div>
            )}

            {/* Error */}
            {pageStatus === 'error' && (
              <div className="pom-state-box">
                <div className="pom-state-icon">⚠️</div>
                <div className="pom-state-title">Error</div>
                <div className="pom-state-msg">{errorMsg}</div>
              </div>
            )}

            {/* No search results */}
            {pageStatus === 'idle' && allStocks.length > 0 && filteredStocks.length === 0 && (
              <div className="pom-state-box">
                <div className="pom-state-icon">🔍</div>
                <div className="pom-state-title">No matching stocks</div>
                <div className="pom-state-msg">No stocks match "<strong>{searchQ}</strong>"</div>
              </div>
            )}

            {/* Idle — no data loaded yet */}
            {pageStatus === 'idle' && allStocks.length === 0 && !metaData && (
              <div className="pom-state-box">
                <div className="pom-state-icon">📭</div>
                <div className="pom-state-title">No data loaded</div>
                <div className="pom-state-msg">
                  Click <strong>Fetch from NSE</strong> to pull live pre-open data,
                  or <strong>Refresh</strong> to load the latest saved record.
                </div>
              </div>
            )}

            {/* Table */}
            {filteredStocks.length > 0 && (
              <table className="pom-table">
                <thead>
                  <tr>
                    {displayCols.map(col => {
                      const isSorted = sortKey === col.key;
                      const icon = isSorted ? (sortDir === 1 ? '↑' : '↓') : '↕';
                      return (
                        <th
                          key={col.key}
                          className={isSorted ? 'sorted' : ''}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                          <span className="pom-sort-icon">{icon}</span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredStocks.map((stock, i) => (
                    <tr key={stock.symbol || i}>
                      {displayCols.map(col => (
                        <td key={col.key}>{renderCell(col, stock)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Toast ── */}
        {toast && (
          <div className={`pom-toast show ${toast.type}`}>{toast.msg}</div>
        )}

        {/* ── Purge modal ── */}
        {purgeOpen && (
          <PurgePreOpenModal
            onClose={() => setPurgeOpen(false)}
            onSubmit={async ({ before, password, category: cat }) => {
              const { data } = await api.post('/pre-open/purge-before', {
                before,
                password,
                category: cat,
              });
              showToast(
                'success',
                `🗑 Purged ${data.deletedCount || 0} snapshots [${data.category}] on/before ${data.before}`
              );
              setPurgeOpen(false);
              await loadLatest(category);
            }}
          />
        )}

      </div>
    </AppLayout>
  );
}

/* ─────────────────────────────────────────────
   Purge modal — 2-step confirm, category-scoped
───────────────────────────────────────────── */
const PURGE_CATEGORIES = [
  { key: 'ALL',     label: 'ALL (every category)' },
  { key: 'NIFTY50', label: 'NIFTY 50'             },
  { key: 'FO',      label: 'F&O'                  },
  { key: 'CASH',    label: 'CASH (All − F&O)'     },
  { key: 'CAS',     label: 'CAS (Closing Auction)'},
];

function PurgePreOpenModal({ onClose, onSubmit }) {
  const [step, setStep] = useState(1);
  const [before, setBefore] = useState('');
  const [password, setPassword] = useState('');
  const [cat, setCat] = useState('ALL');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNext = () => {
    if (!before)             return setError('Pick a cutoff date.');
    if (!password.trim())    return setError('Enter your admin password.');
    setError('');
    setStep(2);
  };

  const handleConfirm = async () => {
    if (confirmText.trim() !== 'DELETE') {
      return setError('Type DELETE exactly to confirm.');
    }
    setLoading(true);
    setError('');
    try {
      await onSubmit({ before, password, category: cat });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err.message || 'Purge failed.';
      setError(msg);
      setStep(1);
      setPassword('');
      setConfirmText('');
    } finally {
      setLoading(false);
    }
  };

  const canClose = !loading;
  const stop = (e) => e.stopPropagation();

  return (
    <div className="pom-modal-backdrop" onClick={canClose ? onClose : undefined}>
      <div className="pom-modal" onClick={stop}>
        <div className="pom-modal-header">
          <h3>🗑 Purge Pre-Open Data — Step {step}/2</h3>
          <button className="pom-modal-close" onClick={canClose ? onClose : undefined}>×</button>
        </div>

        <div className="pom-modal-body">
          <div className="pom-modal-note">
            Permanently deletes snapshots from <code>pre_open_data</code> on or before the selected date for the chosen category.
            <br />
            <strong>The CsvData collections (Nifty50 / FO / Others / CAS) are NOT touched.</strong>
          </div>

          {step === 1 && (
            <>
              <label className="pom-modal-field">
                <span>Category</span>
                <select value={cat} onChange={(e) => setCat(e.target.value)}>
                  {PURGE_CATEGORIES.map(c => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </label>

              <label className="pom-modal-field">
                <span>Delete on or before</span>
                <input
                  type="date"
                  value={before}
                  onChange={(e) => setBefore(e.target.value)}
                  autoFocus
                />
              </label>

              <label className="pom-modal-field">
                <span>Admin Password</span>
                <input
                  type="password"
                  placeholder="Your login password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleNext()}
                />
              </label>
            </>
          )}

          {step === 2 && (
            <>
              <div className="pom-modal-note pom-modal-note-danger">
                This will permanently delete every <code>pre_open_data</code> record
                {' '}with category <strong>{cat}</strong>
                {' '}dated on or before <strong>{before}</strong>. This cannot be undone.
              </div>
              <label className="pom-modal-field">
                <span>Type <code>DELETE</code> to confirm</span>
                <input
                  type="text"
                  placeholder="DELETE"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
                  autoFocus
                />
              </label>
            </>
          )}

          {error && <div className="pom-modal-error">{error}</div>}
        </div>

        <div className="pom-modal-actions">
          <button className="pom-btn pom-btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          {step === 1 && (
            <button className="pom-btn pom-btn-danger" onClick={handleNext}>Next</button>
          )}
          {step === 2 && (
            <>
              <button
                className="pom-btn pom-btn-ghost"
                onClick={() => { setStep(1); setConfirmText(''); setError(''); }}
                disabled={loading}
              >
                Back
              </button>
              <button
                className="pom-btn pom-btn-danger"
                onClick={handleConfirm}
                disabled={loading || confirmText.trim() !== 'DELETE'}
              >
                {loading ? 'Deleting…' : 'Confirm Delete'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
